import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import TmsEventDetailPopover from "@/components/feed/tmsEvent/TmsEventDetailPopover";
import { tmsEventDetailOf } from "@/components/feed/tmsEvent/tmsEventDetail";
import { tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import type { TmsEventRow } from "@/store/types";
import { formatLocalDate } from "@/utils/dateFormat";

/** `HH:mm` in the dispatcher's zone, the clock the catalog's chips carry. */
export function formatChipTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : formatLocalDate(date, "HH:mm");
}

/**
 * One TMS event between a driver's messages, as a compact outlined chip: the whole catalog string as its label and
 * its title, left-aligned like a driver's message and narrower than a message card. An event with detail behind the
 * one line — a version 2 rejection or call — opens it on click; color by category is WEB-2277.
 */
export default function TmsEventChip({
  event,
  formatTime = formatChipTime,
}: {
  event: TmsEventRow;
  formatTime?: (iso: string) => string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const text = tmsEventChipText(event, formatTime);
  const hasDetail = tmsEventDetailOf(event) !== null;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "75%" }}>
      <Chip
        data-sb={TestIds.feed.tmsEventChip}
        size="small"
        variant="outlined"
        title={text}
        label={text}
        onClick={
          hasDetail
            ? (click) => {
                click.stopPropagation();
                setAnchorEl(click.currentTarget);
              }
            : undefined
        }
        sx={{ maxWidth: "100%", height: "auto", py: 0.25, "& .MuiChip-label": { whiteSpace: "normal" } }}
      />
      <TmsEventDetailPopover event={event} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
    </Box>
  );
}
